import {
  ViolationsResponse,
  SuspensionsResponse,
  AppealsResponse,
  StatsResponse,
  WorkerOverviewResponse,
  ViolationFilters,
  SuspensionFilters,
  ViolationFiltersV2,
  ViolationDetailResponse,
  ViolationTimesheetResponse,
  ViolationActivityResponse,
  PendingCountResponse,
  ViolationClearPermissionResponse,
  ViolationApproversResponse,
  ViolationApproverMutationResponse,
  ActionResponse,
  JobViolationsResponse,
} from '../types/accountability';
import { axiosRubyClient as api } from '@/util/axios/axiosClient';

export const accountabilityAPI = {
  // ============================================
  // Tasks Page Endpoints (New)
  // ============================================

  // Get violations list with new filters (search, display_status, sort)
  getViolationsV2: async (
    filters: ViolationFiltersV2,
  ): Promise<ViolationsResponse> => {
    const response = await api.get('/api/admin/accountability/violations', {
      params: filters,
    });
    return response.data;
  },

  // Get single violation detail
  getViolationDetail: async (
    violationId: number,
  ): Promise<ViolationDetailResponse> => {
    const response = await api.get(
      `/api/admin/accountability/violations/${violationId}`,
    );
    return response.data;
  },

  // Get timesheet entries for violation
  getViolationTimesheet: async (
    violationId: number,
  ): Promise<ViolationTimesheetResponse> => {
    const response = await api.get(
      `/api/admin/accountability/violations/${violationId}/timesheet`,
    );
    return response.data;
  },

  // Get activity log for violation
  getViolationActivity: async (
    violationId: number,
  ): Promise<ViolationActivityResponse> => {
    const response = await api.get(
      `/api/admin/accountability/violations/${violationId}/activity`,
    );
    return response.data;
  },

  // Get count of pending violations (for nav badge)
  getPendingCount: async (): Promise<PendingCountResponse> => {
    const response = await api.get(
      '/api/admin/accountability/violations/pending_count',
    );
    return response.data;
  },

  // Get whether current user can clear violations
  getClearPermission: async (): Promise<ViolationClearPermissionResponse> => {
    const response = await api.get(
      '/api/admin/accountability/violations/clear_permission',
    );
    return response.data;
  },

  getViolationApprovers: async (): Promise<ViolationApproversResponse> => {
    const response = await api.get(
      '/api/admin/accountability/violation_approvers',
    );
    return response.data;
  },

  addViolationApprover: async (
    email: string,
  ): Promise<ViolationApproverMutationResponse> => {
    const response = await api.post(
      '/api/admin/accountability/violation_approvers',
      { email },
    );
    return response.data;
  },

  deleteViolationApprover: async (
    id: number,
  ): Promise<ViolationApproverMutationResponse> => {
    const response = await api.delete(
      `/api/admin/accountability/violation_approvers/${id}`,
    );
    return response.data;
  },

  // Export violations to CSV
  exportViolations: async (filters: ViolationFiltersV2): Promise<Blob> => {
    const response = await api.get(
      '/api/admin/accountability/violations/export',
      {
        params: filters,
        responseType: 'blob',
      },
    );
    return response.data;
  },

  // Clear violation (approve appeal / forgive)
  clearViolation: async (
    violationId: number,
    reason: string,
  ): Promise<ActionResponse> => {
    const response = await api.post(
      `/api/admin/accountability/violations/${violationId}/clear`,
      { reason },
    );
    return response.data;
  },

  // Uphold violation (deny appeal / keep active)
  upholdViolation: async (
    violationId: number,
    reason: string,
  ): Promise<ActionResponse> => {
    const response = await api.post(
      `/api/admin/accountability/violations/${violationId}/uphold`,
      { reason },
    );
    return response.data;
  },

  // ============================================
  // Existing Endpoints (for backwards compatibility)
  // ============================================

  // Get violations list with filters
  getViolations: async (
    filters: ViolationFilters,
  ): Promise<ViolationsResponse> => {
    const response = await api.get('/api/admin/accountability/violations', {
      params: filters,
    });
    return response.data;
  },

  // Get suspensions list with filters
  getSuspensions: async (
    filters: SuspensionFilters,
  ): Promise<SuspensionsResponse> => {
    const response = await api.get('/api/admin/accountability/suspensions', {
      params: filters,
    });
    return response.data;
  },

  // Get pending appeals
  getPendingAppeals: async (
    page = 1,
    perPage = 25,
  ): Promise<AppealsResponse> => {
    const response = await api.get(
      '/api/admin/accountability/appeals/pending',
      {
        params: { page, per_page: perPage },
      },
    );
    return response.data;
  },

  // Get system statistics
  getStats: async (
    startDate?: string,
    endDate?: string,
  ): Promise<StatsResponse> => {
    const response = await api.get('/api/admin/accountability/stats', {
      params: { start_date: startDate, end_date: endDate },
    });
    return response.data;
  },

  // Get worker accountability overview
  getWorkerOverview: async (
    workerId: number,
  ): Promise<WorkerOverviewResponse> => {
    const response = await api.get(
      `/api/admin/workers/${workerId}/accountability`,
    );
    return response.data;
  },

  // Get violations for a specific job for tooltip messaging
  getJobViolations: async (jobId: string): Promise<JobViolationsResponse> => {
    const response = await api.get(
      `/api/admin/accountability/jobs/${jobId}/violations`,
    );
    return response.data;
  },

  // Forgive a violation
  forgiveViolation: async (
    violationId: number,
    reason: string,
  ): Promise<ActionResponse> => {
    const response = await api.post(
      `/api/admin/accountability/violations/${violationId}/forgive`,
      { reason },
    );
    return response.data;
  },

  // Remove a violation
  removeViolation: async (
    violationId: number,
    reason: string,
  ): Promise<ActionResponse> => {
    const response = await api.post(
      `/api/admin/accountability/violations/${violationId}/remove`,
      { reason },
    );
    return response.data;
  },

  // Permanently suspend a worker (legacy)
  permanentlySuspendWorker: async (
    workerId: number,
    workerVisibleReason: string,
    internalNotes?: string,
  ): Promise<ActionResponse> => {
    const response = await api.post(
      `/api/admin/accountability/workers/${workerId}/permanent_suspension`,
      {
        worker_visible_reason: workerVisibleReason,
        internal_notes: internalNotes,
      },
    );
    return response.data;
  },

  // Suspend a worker (temporary or permanent based on ends_at)
  suspendWorker: async (
    workerId: number,
    workerVisibleReason: string,
    internalNotes?: string,
    endsAt?: string,
  ): Promise<ActionResponse> => {
    const response = await api.post(
      `/api/admin/accountability/workers/${workerId}/suspend`,
      {
        worker_visible_reason: workerVisibleReason,
        internal_notes: internalNotes,
        ends_at: endsAt,
      },
    );
    return response.data;
  },

  // Lift a suspension
  liftSuspension: async (
    suspensionId: number,
    reason: string,
  ): Promise<ActionResponse> => {
    const response = await api.post(
      `/api/admin/accountability/suspensions/${suspensionId}/lift`,
      { reason },
    );
    return response.data;
  },

  // Approve an appeal
  approveAppeal: async (
    violationId: number,
    notes: string,
  ): Promise<ActionResponse> => {
    const response = await api.post(
      `/api/admin/accountability/appeals/${violationId}/approve`,
      { notes },
    );
    return response.data;
  },

  // Deny an appeal
  denyAppeal: async (
    violationId: number,
    notes: string,
  ): Promise<ActionResponse> => {
    const response = await api.post(
      `/api/admin/accountability/appeals/${violationId}/deny`,
      { notes },
    );
    return response.data;
  },

  // Update violation notes
  updateNotes: async (
    violationId: number,
    notes: string,
  ): Promise<ActionResponse> => {
    const response = await api.post(
      `/api/admin/accountability/violations/${violationId}/update_notes`,
      { admin_notes: notes },
    );
    return response.data;
  },
};
